import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SingleProposal from './Single/SignleProposal';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { enrichExistingProposalHotelsData, getProposalPreview } from 'services/proposal';
import withErrorBoundary from '../withErrorBoundary';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { i18nProposal } from 'utils/shared/utils';
import ProposalCustomize from './ProposalCustomize';
import { EditProposalContext } from 'contexts/EditProposaContext';
import { addLogs } from 'services/user';
import SingleProposalSkeleton from './Single/SingleProposalSkeleton';
import ProposalActions from './ProposalActions';
import TripBuilderProposal from './TB/TripBuilderProposal';
import { isTripBuilderProposal } from 'utils/shared/tripBuilder';
import useRecaptcha from 'hooks/useRecaptcha';
import { MainContext } from 'contexts/MainContext';

const shouldLoadGoogleForProposal = (proposalData: ProposalData) =>
  proposalData.listOfProducts?.some((product) =>
    product.listOfItems?.some(
      (item) =>
        item.proposal?.code === 'HTL' &&
        item.hotels?.some((hotel) => !hotel.serpHotel),
    ),
  );

const ProposalHtml = () => {
  const { i18n } = useTranslation();
  const { user } = useContext(MainContext);
  const { handleRecapcha } = useRecaptcha();
  const [urlParams] = useSearchParams();
  const [proposal, setProposal] = useState<ProposalData>();
  const [config, setConfig] = useState<{ template: string; language: string; sort: string }>();

  const asTripBuilder = urlParams.get('tripBuilder') === 'true';
  const asPlainPreview = urlParams.get('plainPreview') === 'true';

  useEffect(() => {
    const loadProposal = async () => {
      try {
        const res = await getProposalPreview(urlParams.get('id2') as string);

        if (res) {
          let nextProposal = res;
          if (isTripBuilderProposal(res, asPlainPreview, asTripBuilder)) {
            const recaptchaToken = shouldLoadGoogleForProposal(res)
              ? await handleRecapcha()
              : undefined;
            nextProposal = await enrichExistingProposalHotelsData(
              res,
              i18n.language,
              recaptchaToken,
              (updated) => setProposal(JSON.parse(JSON.stringify(updated))),
            );
          }
          setProposal(JSON.parse(JSON.stringify(nextProposal)));

          if (res.externalParam_1) {
            if (urlParams.get('template') || urlParams.get('language')) {
              setConfig({
                template: urlParams.get('template') || 't1',
                language: urlParams.get('language') || 'en',
                sort: urlParams.get('sort') || '',
              });
            } else {
              const params = JSON.parse(res.externalParam_1);
              setConfig({
                template: params.template,
                language: params.language,
                sort: params.sort,
              });
            }
          }
        } else {
          addLogs(`proposal_preview_not_found ${urlParams.get('id')}`);
        }
      } catch (err) {
        console.error('Error loading proposal:', err);
        addLogs(`proposal_preview_error ${urlParams.get('id')} ${err}`);
      }
    };

    loadProposal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.get('id'), urlParams.get('id2'), urlParams.get('plainPreview'), urlParams.get('tripBuilder')]);

  const onChangeStyle = (params: { template: string; language: string; sort: string }) => {
    setConfig({
      template: params.template,
      language: params.language,
      sort: params.sort,
    });
  };

  const providerValue = {
    editMode: false,
    onClickEdit: () => { },
    onClickDelete: () => { },
    onClickEditPrice: () => { },
    proposal: proposal,
  };

  if (proposal && isTripBuilderProposal(proposal, asPlainPreview, asTripBuilder)) {
    return (
      <div className="pr-html pr-html-trip-builder">
        <EditProposalContext.Provider value={providerValue}>
          <TripBuilderProposal
            proposal={proposal}
            onProposalUpdated={setProposal}
          />
        </EditProposalContext.Provider>
      </div>
    );
  }

  return (
    <div className="pr-html">
      <EditProposalContext.Provider value={providerValue}>
        <I18nextProvider i18n={i18nProposal}>
          {proposal && (
            <SingleProposal
              proposal={proposal}
              template={config?.template || 't1'}
              sort={config?.sort || '1'}
            />
          )}
          {!proposal && <SingleProposalSkeleton />}
          <ProposalActions proposal={proposal} />
          {user?.userCode === proposal?.userU && (
            <ProposalCustomize onChange={onChangeStyle} config={config} />
          )}
        </I18nextProvider>
      </EditProposalContext.Provider>
    </div>
  );
};

export default withErrorBoundary(ProposalHtml, 'ProposalHtml');
